/* eslint-disable import/no-unresolved,react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useMutation, useQuery } from 'react-query';
import { OnboardingBodyLayout, TFASecurityComponent } from 'assets/components';
import Countdown from '../../../utilities/counter';
import { OnboardingContext } from '../../../contexts/onboarding/context';
import { RECAPTCHA_EVENTS } from '../constants';
import {
  PHONE_VERIFICATION_CHECK,
  PHONE_VERIFICATION_SEND,
  PHONE_VERIFICATION_SEND_AUTHY,
  TOTP_EMAIL_VERIFICATION_CHECK,
  USER_LOGIN,
} from '../mutations';
import { FLOWS, aliasUser, identifyUser, sendStats } from '../../../services/mixpanel';
import {
  MXP_RESET_PASSWORD_PHONE_CONFIRM_CODE,
  MXP_SIGN_IN_SUCCESS,
  MXP_SIGN_IN_TFA_CONFIRM,
  MXP_SIGN_IN_TFA_REQUEST,
  MXP_SIGN_IN_TFA_RESEND,
  MXP_SIGN_IN_TFA_VISIT,
} from '../mixpanel-events';
import { showAlert } from '../../../utilities/notifications';
import { ACTIONS_AFTER_RESET_SECRET } from '../../SecurityTOTP/constants';
import { sharedSetting } from '../../../utilities/constants';
import { encryptWithPublicKey } from '../../../utilities/helpers';
import { useSetting } from '../../../utilities/sharedHooks/settings';
import { PASSWORD_PUBLIC_KEY } from '../../../utilities/sharedQueries';
import RecaptchaModal from '../../shared/RecaptchaModal';
import { cacheRecaptchaV3failure, clearRecaptchaV3failure } from '../../../utilities/caching';
import { useUsEnv } from '../../../utilities/sharedHooks/useUsEnv';
import { OAUTH_SESSION_KEYS } from '../../Oauth/constants';
import { CARD_ORDER_DEEPLINK_SESSION_KEYS } from '../../Card/deeplink/constants';
import { WITHDRAWAL_DEEPLINK_SESSION_KEYS } from '../../Accounts/deeplink/constants';
import { TOPUP_DEEPLINK_SESSION_KEYS } from '../../Accounts/topupDeeplink/constants';
import { CASHBACK_DEEPLINK_SESSION_KEYS } from '../../Card/Loyalty/deeplink/constants';
import { SET_PIN_DEEPLINK_SESSION_KEYS } from '../../Card/Settings/deeplink/constants';
import { MANAGE_ASSETS_DEEPLINK_SESSION_KEYS } from '../../Accounts/manageAssetsDeeplink/constants';

const phoneCountdown = new Countdown();

const TFASecurity = () => {
  const { state } = useLocation();
  const history = useHistory();

  const { t: translate } = useTranslation();

  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isOpenRecV2, setIsOpenRecV2] = useState(false);
  const [recV2Token, setRecV2Token] = useState('');

  const { isUsEnv } = useUsEnv();
  const oauthReturnUrl = sessionStorage.getItem(
    isUsEnv ? OAUTH_SESSION_KEYS.US_RETURN_URL : OAUTH_SESSION_KEYS.ROW_RETURN_URL
  );
  const withdrawalReturnUrl = sessionStorage.getItem(WITHDRAWAL_DEEPLINK_SESSION_KEYS.RETURN_URL);
  const cardOrderReturnUrl = sessionStorage.getItem(CARD_ORDER_DEEPLINK_SESSION_KEYS.RETURN_URL);
  const topupReturnUrl = sessionStorage.getItem(TOPUP_DEEPLINK_SESSION_KEYS.RETURN_URL);
  const cashbackReturnUrl = sessionStorage.getItem(CASHBACK_DEEPLINK_SESSION_KEYS.RETURN_URL);
  const setPinReturnUrl = sessionStorage.getItem(SET_PIN_DEEPLINK_SESSION_KEYS.RETURN_URL);
  const manageAssetsReturnUrl = sessionStorage.getItem(
    MANAGE_ASSETS_DEEPLINK_SESSION_KEYS.RETURN_URL
  );

  const [isLoginRequest, setIsLoginRequest] = useState(false);
  const [isCheckingPhoneNumberLoading, setIsCheckingPhoneNumberLoading] = useState(false);

  const { state: onboardingState } = useContext(OnboardingContext);

  const isResetPasswordFlow = state?.resetPasswordFlow;

  const [timer, setTimer] = useState();
  const [form, setForm] = useState({
    confirmed_phone_number: `${state?.country_code}${state?.phone_number}`,
    code: '',
    verification_code: '',
    via: sharedSetting.MODES_2FA.sms,
  });

  const totpFeatureEnabled = onboardingState?.mainSettings?.feature_allow_totp;

  const isTOTP = state?.totp_required || state?.isTOTP;

  const [enablePasswordKMSEncryption] = useSetting(
    'app_features',
    'feature_toggle_enable_password_kms_encryption',
    false,
    false
  );

  const { data: publicKeyData } = useQuery(PASSWORD_PUBLIC_KEY, {
    enabled: !!enablePasswordKMSEncryption,
  });

  const { mutate: requestPhoneVerification, isLoading: isLoadingRequestPhoneNumberVerification } =
    useMutation(PHONE_VERIFICATION_SEND_AUTHY, {
      onSettled: (_, error) => {
        sendStats(MXP_SIGN_IN_TFA_REQUEST, {}, FLOWS.SIGN_IN);
        if (error) {
          if (error?.response?.status === 307) {
            setIsOpenRecV2(true);
            cacheRecaptchaV3failure();
          } else {
            setIsOpenRecV2(false);
            clearRecaptchaV3failure();
            showAlert({ message: error.message, type: 'danger' });
          }
        } else {
          setIsOpenRecV2(false);
          clearRecaptchaV3failure();
          phoneCountdown.start();
        }
      },
    });

  const {
    mutate: requestPhoneVerificationResetPassword,
    isLoading: isLoadingPhoneVerificationResetPassword,
  } = useMutation(PHONE_VERIFICATION_SEND, {
    onSettled: (data, error) => {
      if (error) {
        if (error?.response?.status === 307) {
          setIsOpenRecV2(true);
          cacheRecaptchaV3failure();
        } else {
          setIsOpenRecV2(false);
          clearRecaptchaV3failure();
          showAlert({ message: error.message, type: 'danger' });
        }
      } else {
        setIsOpenRecV2(false);
        clearRecaptchaV3failure();
        phoneCountdown.start();
      }
    },
  });

  const { mutate: login, isLoading: isLoadingLogin } = useMutation(USER_LOGIN, {
    onSettled: (data, error) => {
      if (error) {
        if (error?.response?.status === 307) {
          setIsOpenRecV2(true);
          cacheRecaptchaV3failure();
        } else {
          setIsOpenRecV2(false);
          clearRecaptchaV3failure();
          showAlert({ message: error.message, type: 'danger' });
        }
      } else {
        setIsOpenRecV2(false);
        clearRecaptchaV3failure();
        sendStats(
          MXP_SIGN_IN_SUCCESS,
          { tfaRequired: data?.['2fa_required'], success: !error },
          FLOWS.SIGN_IN
        );
        aliasUser(data);
        identifyUser(data);

        if (!data?.user?.totp_enabled && totpFeatureEnabled && data?.user?.totp_active) {
          history.push(`/onboarding/totp`, { email: state?.username });
        } else {
          if (oauthReturnUrl) {
            history.push(oauthReturnUrl);
            return;
          }
          if (withdrawalReturnUrl) {
            sessionStorage.removeItem(WITHDRAWAL_DEEPLINK_SESSION_KEYS.RETURN_URL);
            history.push(withdrawalReturnUrl);
            return;
          }
          if (cardOrderReturnUrl) {
            sessionStorage.removeItem(CARD_ORDER_DEEPLINK_SESSION_KEYS.RETURN_URL);
            history.push(cardOrderReturnUrl);
            return;
          }
          if (topupReturnUrl) {
            sessionStorage.removeItem(TOPUP_DEEPLINK_SESSION_KEYS.RETURN_URL);
            history.push(topupReturnUrl);
            return;
          }
          if (cashbackReturnUrl) {
            sessionStorage.removeItem(CASHBACK_DEEPLINK_SESSION_KEYS.RETURN_URL);
            history.push(cashbackReturnUrl);
            return;
          }
          if (setPinReturnUrl) {
            sessionStorage.removeItem(SET_PIN_DEEPLINK_SESSION_KEYS.RETURN_URL);
            history.push(setPinReturnUrl);
            return;
          }
          if (manageAssetsReturnUrl) {
            sessionStorage.removeItem(MANAGE_ASSETS_DEEPLINK_SESSION_KEYS.RETURN_URL);
            history.push(manageAssetsReturnUrl);
            return;
          }
          history.push(`/`, { showPromo: true });
        }
      }
    },
  });

  const { mutate: checkTOTPToken, isLoading: chekcingTOTPVerification } = useMutation(
    TOTP_EMAIL_VERIFICATION_CHECK,
    {
      onSettled: (data, error) => {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        } else {
          history.push(`change/${state?.resetKey}`, {
            ...state,
          });
        }
      },
    }
  );

  const { mutate: checkPhoneNumberVerification, isLoading: checkingPhoneNumberVerification } =
    useMutation(PHONE_VERIFICATION_CHECK, {
      onSettled: (data, error) => {
        if (error) {
          if (error?.response?.status === 307) {
            setIsOpenRecV2(true);
            cacheRecaptchaV3failure();
          } else {
            setIsOpenRecV2(false);
            clearRecaptchaV3failure();
            showAlert({ message: error.message, type: 'danger' });
          }
        } else {
          setIsOpenRecV2(false);
          clearRecaptchaV3failure();
          sendStats(MXP_RESET_PASSWORD_PHONE_CONFIRM_CODE, {}, FLOWS.PASSWORD_RESET);
          history.push(`change/${state?.resetKey}`, {
            ...state,
          });
        }
      },
    });

  const isLoading = useMemo(
    () => checkingPhoneNumberVerification || chekcingTOTPVerification || isLoadingLogin,
    [checkingPhoneNumberVerification, isLoadingLogin, chekcingTOTPVerification]
  );

  const handleRequestPhoneNumberVerification = async () => {
    try {
      const token = await executeRecaptcha(RECAPTCHA_EVENTS.VERIFY_PHONE_NUMBER);
      if (!token) {
        return false;
      }

      if (isResetPasswordFlow) {
        requestPhoneVerificationResetPassword({
          ...state,
          token,
          verificationId: state?.verification_id,
        });
      } else {
        sendStats(MXP_SIGN_IN_TFA_RESEND, {}, FLOWS.SIGN_IN);
        requestPhoneVerification({ ...state, token });
      }
    } catch (err) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (!isTOTP && (!state?.country_code || !state?.phone_number)) {
      history.push('/onboarding/login');
    } else if (isTOTP && !state?.email && !state?.username) {
      history.push('/onboarding/login');
    }
  }, []);

  useEffect(() => {
    if (!isTOTP) {
      phoneCountdown.bind(setTimer);
      handleRequestPhoneNumberVerification();
    }
    return () => {
      if (!isTOTP) phoneCountdown.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTOTP]);

  useEffect(() => {
    if (!isResetPasswordFlow) {
      sendStats(MXP_SIGN_IN_TFA_VISIT, {}, FLOWS.SIGN_IN);
    }
  }, []);

  const handleChange = (e) => {
    if (!e || !e.target) return;
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const sendData = {
      username: state.username,
      password: state.password,
      otpauth: form.code,
    };

    if (isResetPasswordFlow) {
      form.verification_code = form.code;
      try {
        setIsCheckingPhoneNumberLoading(true);
        const token = await executeRecaptcha(RECAPTCHA_EVENTS.SIGNUP_CHECK_PHONE_NUMBER);
        if (!token) {
          return false;
        }
        if (isTOTP) {
          checkTOTPToken({ email: state?.email, token: form.code });
        } else {
          checkPhoneNumberVerification({
            ...form,
            ...state,
            verification_id: state?.verification_id,
            token,
          });
        }
      } catch (error) {
        return false;
      }
    } else {
      try {
        setIsLoginRequest(true);
        const token = await executeRecaptcha(RECAPTCHA_EVENTS.LOGIN);
        if (!token) {
          return false;
        }
        sendStats(MXP_SIGN_IN_TFA_CONFIRM, { ...sendData }, FLOWS.SIGN_IN);
        if (enablePasswordKMSEncryption) {
          const encryptedPassword = await encryptWithPublicKey(
            publicKeyData.publicKey,
            state.password
          );
          login({ ...sendData, password: encryptedPassword, token });
        } else {
          login({ ...sendData, token });
        }
      } catch (err) {
        return false;
      }
    }

    return true;
  };

  const renderButtons = () => (
    <>
      <div className="d-none d-md-block w-100">
        {!isTOTP && (
          <button
            className="btn btn-primary w-100 text-bold"
            type="submit"
            disabled={form.code === '' || isLoading}
            loading={isLoading ? 'true' : 'false'}
            form="confirm-phone-form">
            {translate('confirm_button_label')}
          </button>
        )}
        {isTOTP && (
          <>
            <div
              className="text-center cursor-pointer"
              tabIndex={-1}
              role="presentation"
              onClick={() => {
                history.push(`/onboarding/totp-warning-reset-secret`, {
                  ...state,
                  action: state?.resetPasswordFlow
                    ? ACTIONS_AFTER_RESET_SECRET.RESET_PASSWORD
                    : ACTIONS_AFTER_RESET_SECRET.LOGIN,
                });
              }}>
              <p className="text-primary">{translate('totp_lost_app_label')}</p>
            </div>
            <button
              className="btn btn-primary w-100 mt-5 text-bold"
              type="submit"
              disabled={form.code === '' || isLoading}
              loading={isLoading ? 'true' : 'false'}
              form="confirm-phone-form">
              {translate('confirm_button_label')}
            </button>
          </>
        )}
      </div>
      <div className="d-block d-md-none position-fixed bottom-0 bg-lighter w-100 py-3 px-3">
        {!isTOTP && (
          <button
            className="btn btn-primary w-100 text-bold"
            type="submit"
            disabled={form.code === '' || isLoading}
            loading={isLoading ? 'true' : 'false'}
            form="confirm-phone-form">
            {translate('confirm_button_label')}
          </button>
        )}
        {isTOTP && (
          <>
            <div
              className="text-center cursor-pointer"
              tabIndex={-1}
              role="presentation"
              onClick={() => {
                history.push(`/onboarding/totp-warning-reset-secret`, {
                  ...state,
                  action: state?.resetPasswordFlow
                    ? ACTIONS_AFTER_RESET_SECRET.RESET_PASSWORD
                    : ACTIONS_AFTER_RESET_SECRET.LOGIN,
                });
              }}>
              <p className="text-primary">{translate('totp_lost_app_label')}</p>
            </div>
            <button
              className="btn btn-primary w-100 mt-5 text-bold"
              type="submit"
              disabled={form.code === '' || isLoading}
              loading={isLoading ? 'true' : 'false'}
              form="confirm-phone-form">
              {translate('confirm_button_label')}
            </button>
          </>
        )}
      </div>
    </>
  );

  const onChangeRecV2 = async (tokenV2) => {
    setRecV2Token(tokenV2);
    const sendData = {
      username: state.username,
      password: state.password,
      otpauth: form.code,
    };
    if (isLoginRequest) {
      setIsLoginRequest(false);
      sendStats(MXP_SIGN_IN_TFA_CONFIRM, { ...sendData }, FLOWS.SIGN_IN);
      if (enablePasswordKMSEncryption) {
        const encryptedPassword = await encryptWithPublicKey(
          publicKeyData.publicKey,
          state.password
        );
        login({ ...sendData, password: encryptedPassword, token: tokenV2 });
      } else {
        login({ ...sendData, token: tokenV2 });
      }
    } else if (isCheckingPhoneNumberLoading && !isTOTP) {
      setIsCheckingPhoneNumberLoading(false);
      checkPhoneNumberVerification({
        ...form,
        ...state,
        verification_id: state?.verification_id,
        token: tokenV2,
      });
    } else if (isResetPasswordFlow) {
      requestPhoneVerificationResetPassword({
        ...state,
        token: tokenV2,
        verificationId: state?.verification_id,
      });
    } else {
      sendStats(MXP_SIGN_IN_TFA_RESEND, {}, FLOWS.SIGN_IN);
      requestPhoneVerification({ ...state, token: tokenV2 });
    }
  };

  return (
    <>
      <OnboardingBodyLayout
        renderButtons={renderButtons}
        title={translate('2TFA_page_title')}
        withBack
        onPrevClick={() => {
          if (isResetPasswordFlow) {
            history.push('/onboarding/login');
            return;
          }
          history.goBack();
        }}>
        <TFASecurityComponent
          form={form}
          state={state}
          timer={timer}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          handleResendOnClick={handleRequestPhoneNumberVerification}
          renderButtons={renderButtons}
          isLoading={isLoadingRequestPhoneNumberVerification}
          isTOTP={isTOTP}
        />
      </OnboardingBodyLayout>
      <RecaptchaModal
        onChange={onChangeRecV2}
        isLoading={
          isLoading ||
          isLoadingRequestPhoneNumberVerification ||
          isLoadingPhoneVerificationResetPassword
        }
        resetToken={() => setRecV2Token('')}
        token={recV2Token}
        setIsModalVisible={setIsOpenRecV2}
        isModalVisible={isOpenRecV2}
      />
    </>
  );
};

export default TFASecurity;
